/**
 * QuickTrackCompareDrawer — side-by-side birds-eye triage.
 *
 * Fans out to POST /api/candidates/quick-track-compare for 2–6 candidates
 * and renders the same 4 sections (Scores / Salary / Applications /
 * Interviews / Follow-ups) as ROWS with candidates as COLUMNS. The
 * strongest option per row gets an amber "★ best" ribbon, sourced from
 * the backend's per-metric winner lookup — so the FE doesn't have to
 * re-derive "highest score" / "cheapest to hire" / "shortest notice".
 *
 * Wide drawer (~90vw) to keep 5 candidate columns readable. Closes on
 * backdrop / Escape / X-button.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import {
    X, Sparkles, Wallet, Star, ThumbsUp, ThumbsDown, CalendarClock,
    MessageSquare, Loader2, Trophy,
} from "lucide-react";

function inr(v, currency = "INR") {
    if (v == null || v === "") return "—";
    try {
        return new Intl.NumberFormat("en-IN", {
            style: "currency", currency, maximumFractionDigits: 0,
        }).format(v);
    } catch { return `${v}`; }
}

function timeAgo(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
}

/** Pill styling for the follow-up status enum (mirrors QuickTrackDrawer). */
function followupStatusClasses(status) {
    const s = (status || "").toLowerCase();
    if (s.includes("interested") && !s.includes("not")) return "border-emerald-500 text-emerald-800 bg-emerald-50";
    if (s.includes("initial contact")) return "border-emerald-500 text-emerald-800 bg-emerald-50";
    if (s.includes("call back")) return "border-blue-400 text-blue-700 bg-blue-50";
    if (s.includes("not interested")) return "border-red-400 text-red-700 bg-red-50";
    if (s.includes("not reachable") || s.includes("switched off") || s.includes("not received") || s.includes("unable to contact")) return "border-amber-500 text-amber-800 bg-amber-50";
    return "border-border text-muted-foreground bg-neutral-50";
}

/** Small ribbon rendered inside a cell when this candidate wins the row's metric. */
function BestRibbon({ label = "best" }) {
    return (
        <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 bg-amber-100 text-amber-900 border border-amber-400"
            data-testid="qt-compare-best-ribbon">
            <Trophy className="w-2.5 h-2.5" /> {label}
        </span>
    );
}

/** RowHeader — the leftmost sticky column with the metric label + icon. */
function RowHeader({ Icon, label }) {
    return (
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <Icon className="w-3.5 h-3.5" />
            {label}
        </div>
    );
}

export default function QuickTrackCompareDrawer({ candidateIds, open, onClose }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !candidateIds?.length) return;
        let cancelled = false;
        (async () => {
            setLoading(true); setErr(null);
            try {
                const { data } = await api.post("/candidates/quick-track-compare", {
                    candidate_ids: candidateIds,
                });
                if (!cancelled) setData(data);
            } catch (e) {
                if (!cancelled) setErr(formatApiError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, candidateIds]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    // Mirror QuickTrackDrawer — silence lingering HoverCards while any
    // Quick Track drawer is open (see index.css `body.qt-drawer-open`).
    useEffect(() => {
        if (!open) return;
        document.body.classList.add("qt-drawer-open");
        return () => document.body.classList.remove("qt-drawer-open");
    }, [open]);

    if (!open) return null;

    const items = data?.items || [];
    const best = data?.best || {};

    // Grid template: sticky-left row-header column + N candidate columns.
    const gridStyle = {
        gridTemplateColumns: `220px repeat(${Math.max(items.length, 1)}, minmax(220px, 1fr))`,
    };

    return createPortal((
        <div
            className="fixed inset-0 z-[110] flex items-stretch justify-end"
            data-testid="quick-track-compare-drawer"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <button
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 bg-black/50"
                data-testid="qt-compare-backdrop"
            />
            <aside
                onClick={(e) => e.stopPropagation()}
                className="absolute right-0 top-0 bottom-0 w-full lg:w-[85vw] xl:w-[80vw] bg-neutral-50 border-l border-border shadow-2xl overflow-hidden flex flex-col"
            >
                {/* Header */}
                <div className="bg-white border-b border-border px-5 py-3 flex items-center gap-3 shrink-0">
                    <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-900 border border-amber-400 inline-flex items-center justify-center">
                        <Sparkles className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Quick Track — Compare</div>
                        <div className="font-semibold text-sm">
                            {loading ? "Loading…" : `${items.length} candidate${items.length !== 1 ? "s" : ""} side-by-side`}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 inline-flex items-center justify-center hover:bg-neutral-100 border border-border"
                        data-testid="qt-compare-close"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {loading && !data && (
                    <div className="flex-1 flex items-center justify-center text-muted-foreground text-xs">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fanning out to {candidateIds?.length || 0} candidates…
                    </div>
                )}
                {err && (
                    <div className="m-4 border border-red-300 bg-red-50 p-3 text-xs text-red-800" data-testid="qt-compare-error">{err}</div>
                )}

                {data && (
                    <div className="flex-1 overflow-auto pw-thin-scroll">
                        <div className="min-w-max grid" style={gridStyle}>
                            {/* Column headers — candidate name row */}
                            <div className="bg-white border-b border-border sticky top-0 z-10" />
                            {items.map((it) => (
                                <div key={it.candidate?.id} className="bg-white border-b border-l border-border px-3 py-2 sticky top-0 z-10" data-testid={`qt-compare-col-${it.candidate?.id}`}>
                                    <div className="font-semibold text-sm truncate">{it.candidate?.name || "Unknown"}</div>
                                    <div className="text-[10px] text-muted-foreground truncate">{it.candidate?.current_title || "—"}</div>
                                    <div className="text-[10px] font-mono text-muted-foreground truncate">{it.candidate?.location || ""}</div>
                                </div>
                            ))}

                            {/* Scores row */}
                            <div className="bg-neutral-100 border-b border-border px-3 py-3">
                                <RowHeader Icon={Star} label="AI Score" />
                            </div>
                            {items.map((it) => {
                                const cid = it.candidate?.id;
                                const isBest = best.highest_ai_score === cid;
                                return (
                                    <div key={cid} className={`border-b border-l border-border px-3 py-3 ${isBest ? "bg-amber-50" : "bg-white"}`}>
                                        <div className="flex items-center">
                                            <span className="font-display font-black text-xl tabular-nums">{it.scores?.latest_ai_score ?? "—"}</span>
                                            {isBest && <BestRibbon />}
                                        </div>
                                        <div className="text-[10px] font-mono text-muted-foreground mt-0.5">avg {it.scores?.avg_ai_score ?? "—"}</div>
                                    </div>
                                );
                            })}

                            {/* Recruiter row */}
                            <div className="bg-neutral-100 border-b border-border px-3 py-3">
                                <RowHeader Icon={ThumbsUp} label="Recruiter" />
                            </div>
                            {items.map((it) => {
                                const cid = it.candidate?.id;
                                const isBest = best.highest_recruiter === cid;
                                return (
                                    <div key={cid} className={`border-b border-l border-border px-3 py-3 ${isBest ? "bg-amber-50" : "bg-white"}`}>
                                        <div className="flex items-center">
                                            <span className="font-display font-black text-xl tabular-nums">{it.scores?.latest_recruiter_score ?? "—"}</span>
                                            {isBest && <BestRibbon />}
                                        </div>
                                        <div className="text-[10px] font-mono text-muted-foreground mt-0.5 flex items-center gap-2">
                                            <span className="text-emerald-700"><ThumbsUp className="w-2.5 h-2.5 inline" /> {it.scores?.votes?.up || 0}</span>
                                            <span className="text-red-700"><ThumbsDown className="w-2.5 h-2.5 inline" /> {it.scores?.votes?.down || 0}</span>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Salary row */}
                            <div className="bg-neutral-100 border-b border-border px-3 py-3">
                                <RowHeader Icon={Wallet} label="Salary" />
                            </div>
                            {items.map((it) => {
                                const cid = it.candidate?.id;
                                const isCheapest = best.lowest_desired_ctc === cid;
                                const s = it.salary || {};
                                const desired = (s.desired_ctc_min || s.desired_ctc_max)
                                    ? `${inr(s.desired_ctc_min, s.currency)}${s.desired_ctc_max ? ` – ${inr(s.desired_ctc_max, s.currency)}` : ""}`
                                    : "—";
                                return (
                                    <div key={cid} className={`border-b border-l border-border px-3 py-3 ${isCheapest ? "bg-amber-50" : "bg-white"}`}>
                                        <div className="text-xs">
                                            <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Current</div>
                                            <div className="font-semibold">{inr(s.current_ctc, s.currency)}</div>
                                        </div>
                                        <div className="text-xs mt-2 flex items-center gap-1">
                                            <div>
                                                <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Desired</div>
                                                <div className="font-semibold">{desired}</div>
                                            </div>
                                            {isCheapest && <BestRibbon label="cheapest" />}
                                        </div>
                                        {s.delta_pct != null && (
                                            <div className={`mt-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 border ${s.delta_pct > 30 ? "border-amber-500 text-amber-800 bg-amber-50" : "border-emerald-500 text-emerald-800 bg-emerald-50"}`}>
                                                {s.delta_pct > 0 ? "+" : ""}{s.delta_pct}%
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Notice period row */}
                            <div className="bg-neutral-100 border-b border-border px-3 py-3">
                                <RowHeader Icon={CalendarClock} label="Notice period" />
                            </div>
                            {items.map((it) => {
                                const cid = it.candidate?.id;
                                const isShortest = best.shortest_notice === cid;
                                const nd = it.salary?.notice_period_days;
                                return (
                                    <div key={cid} className={`border-b border-l border-border px-3 py-3 ${isShortest ? "bg-amber-50" : "bg-white"}`}>
                                        <div className="flex items-center">
                                            <span className="font-semibold text-lg tabular-nums">{nd != null ? `${nd}d` : "—"}</span>
                                            {isShortest && <BestRibbon label="fastest" />}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Interviews row */}
                            <div className="bg-neutral-100 border-b border-border px-3 py-3">
                                <RowHeader Icon={CalendarClock} label="Interviews" />
                            </div>
                            {items.map((it) => (
                                <div key={it.candidate?.id} className="bg-white border-b border-l border-border px-3 py-3">
                                    <div className="text-xs">
                                        {(it.interviews || []).slice(0, 3).map((iv) => (
                                            <div key={iv.id} className="mb-1">
                                                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">R{iv.round || "—"}</span>{" "}
                                                <span className={`text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 border ${
                                                    (iv.status || "").toLowerCase() === "completed" ? "border-emerald-500 text-emerald-800 bg-emerald-50" :
                                                    (iv.status || "").toLowerCase() === "cancelled" ? "border-red-400 text-red-700 bg-red-50" :
                                                    "border-blue-400 text-blue-700 bg-blue-50"
                                                }`}>{iv.status || "—"}</span>
                                                {iv.rating != null && <span className="ml-1 text-muted-foreground">·{iv.rating}</span>}
                                            </div>
                                        ))}
                                        {(it.interviews || []).length === 0 && <span className="text-muted-foreground">—</span>}
                                    </div>
                                </div>
                            ))}

                            {/* Timeline preview row (3 most recent) */}
                            <div className="bg-neutral-100 border-b border-border px-3 py-3">
                                <RowHeader Icon={MessageSquare} label="Recent activity" />
                            </div>
                            {items.map((it) => (
                                <div key={it.candidate?.id} className="bg-white border-b border-l border-border px-3 py-3">
                                    {(it.timeline || []).slice(0, 3).map((t, i) => (
                                        <div key={i} className="mb-1.5 text-xs">
                                            <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2 flex-wrap">
                                                <span>{t.kind === "followup" ? "follow-up" : t.kind}</span>
                                                {t.kind === "followup" && t.meta?.status && (
                                                    <span className={`px-1 py-0.5 border ${followupStatusClasses(t.meta.status)}`}>
                                                        {t.meta.status}
                                                    </span>
                                                )}
                                                <span className="ml-auto">{timeAgo(t.when)}</span>
                                            </div>
                                            {t.body && <div className="text-[11px] text-foreground/80 line-clamp-2">{t.body}</div>}
                                        </div>
                                    ))}
                                    {(it.timeline || []).length === 0 && <div className="text-xs text-muted-foreground">—</div>}
                                </div>
                            ))}

                            {/* Counts row */}
                            <div className="bg-neutral-100 border-b border-border px-3 py-3">
                                <RowHeader Icon={Sparkles} label="Counts" />
                            </div>
                            {items.map((it) => {
                                const c = it.counts || {};
                                return (
                                    <div key={it.candidate?.id} className="bg-white border-b border-l border-border px-3 py-3">
                                        <div className="grid grid-cols-5 gap-1 text-center">
                                            {[
                                                ["Notes", c.notes || 0],
                                                ["F/Ups", c.followups || 0],
                                                ["Emails", c.emails || 0],
                                                ["SMS", c.sms || 0],
                                                ["IVs", (c.interviews_scheduled || 0) + (c.interviews_completed || 0)],
                                            ].map(([label, n]) => (
                                                <div key={label} className="border border-border p-1">
                                                    <div className="text-[9px] font-mono uppercase text-muted-foreground">{label}</div>
                                                    <div className="text-sm font-semibold tabular-nums">{n}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </aside>
        </div>
    ), document.body);
}
